import Phaser from 'phaser';
import { COLORS, FONTS, GAME_WIDTH, fontStr } from '@/config';
import { RoundedRect } from '@/ui/RoundedRect';
import { gameState } from '@/state/store';
import type { GameStateData } from '@/state/GameState';
import { SESONG_NAVN, aaret, sesongForDag } from '@/systems/sesong';
import { audioManager } from '@/systems/audioManager';
import { ambientAudio } from '@/systems/ambientAudio';
import { autoSpeed } from '@/systems/autoSpeed';
import { sfx } from '@/systems/sfx';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';

const HUD_HEIGHT = 96;

export class HUDScene extends Phaser.Scene {
  private kronerText?: Phaser.GameObjects.Text;
  private dagText?: Phaser.GameObjects.Text;
  private sesongText?: Phaser.GameObjects.Text;
  private tierChipText?: Phaser.GameObjects.Text;
  private tierGlow?: RoundedRect;
  private tierGlowTween?: Phaser.Tweens.Tween;
  private unsubscribe?: () => void;

  constructor() {
    super('HUD');
  }

  create(): void {
    this.add
      .rectangle(0, 0, GAME_WIDTH, HUD_HEIGHT, COLORS.white)
      .setOrigin(0, 0)
      .setAlpha(0.98);

    this.add
      .rectangle(0, HUD_HEIGHT, GAME_WIDTH, 4, COLORS.wine)
      .setOrigin(0, 0)
      .setAlpha(0.9);

    // 3 primær-kolonner: Kroner, Dag, Sesong.
    // Tier + omdømme samles i én sekundær-chip øverst til høyre.
    const col1 = 28;
    const col2 = Math.round(GAME_WIDTH * 0.36);
    const col3 = Math.round(GAME_WIDTH * 0.50);

    this.buildStat(
      col1,
      'Kroner',
      (s) => this.formatKroner(s.kroner),
      (t) => (this.kronerText = t),
    );
    this.buildStat(col2, 'Dag', (s) => String(s.dag), (t) => (this.dagText = t));
    this.buildStat(
      col3,
      'Sesong',
      (s) => sesongLabel(s.dag),
      (t) => (this.sesongText = t),
    );

    this.buildTierChip();
    this.buildMuteKnapp();
    this.buildSpeedKnapp();

    this.unsubscribe = gameState.subscribe((snap) => this.refresh(snap));

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubscribe?.());
  }

  private buildTierChip(): void {
    const chipX = GAME_WIDTH - 200;
    const chipY = 14;
    const snap = gameState.snapshot;
    // Runde 40: rounded glow-rektangel under tier-chip. Origin (1, 0.5) er
    // ikke direkte støttet av RoundedRect (alltid 0,0) — kompenserer ved
    // å justere posisjon manuelt.
    const glowW = 220;
    const glowH = 52;
    this.tierGlow = new RoundedRect({
      scene: this,
      x: chipX - glowW,
      y: chipY + 26 - glowH / 2,
      width: glowW,
      height: glowH,
      radius: 14,
      fillColor: 0xb8860b,
      fillAlpha: 0,
    });
    this.tierChipText = this.add
      .text(chipX, chipY, tierChipLabel(snap), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#3a2e22',
        backgroundColor: 'rgba(244,234,213,0.95)',
        padding: { left: 12, right: 12, top: 6, bottom: 6 },
        fontStyle: '700',
      })
      .setOrigin(1, 0);
  }

  glodTierChip(varighetMs = 3000): void {
    if (!this.tierGlow) return;
    if (foretrekkerRedusertBevegelse()) {
      return;
    }
    this.tierGlowTween?.stop();
    this.tierGlow.setFillStyle(0xb8860b, 0);
    // Tween på en proxy-verdi siden RoundedRect tegner i Graphics og
    // ikke har en innebygd "alpha"-property som påvirker fill direkte.
    const proxy = { v: 0 };
    this.tierGlowTween = this.tweens.add({
      targets: proxy,
      v: { from: 0, to: 0.85 },
      duration: 500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: Math.max(0, Math.floor(varighetMs / 1000) - 1),
      onUpdate: () => {
        this.tierGlow?.setFillStyle(0xb8860b, proxy.v);
      },
      onComplete: () => {
        this.tierGlow?.setFillStyle(0xb8860b, 0);
      },
    });
  }

  private buildMuteKnapp(): void {
    const x = GAME_WIDTH - 16;
    const y = HUD_HEIGHT - 8;
    const knapp = this.add
      .text(x, y, this.muteSymbol(), {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#8B5E3C',
        backgroundColor: 'rgba(244,234,213,0.85)',
        padding: { left: 8, right: 8, top: 2, bottom: 2 },
      })
      .setOrigin(1, 1)
      .setInteractive({ useHandCursor: true });
    knapp.on('pointerup', () => {
      const erAvNa = audioManager.toggleMute();
      // Mute-knappen styrer både musikk, SFX og ambient som én "alt eller intet".
      if (erAvNa) {
        ambientAudio.slaaAv();
      } else {
        ambientAudio.slaaPaa();
      }
      knapp.setText(this.muteSymbol());
    });
  }

  private buildSpeedKnapp(): void {
    const x = GAME_WIDTH - 76;
    const y = HUD_HEIGHT - 8;
    const knapp = this.add
      .text(x, y, autoSpeed.symbol(), {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#8B5E3C',
        backgroundColor: 'rgba(244,234,213,0.85)',
        padding: { left: 8, right: 8, top: 2, bottom: 2 },
      })
      .setOrigin(1, 1)
      .setInteractive({ useHandCursor: true });
    knapp.on('pointerup', () => {
      autoSpeed.cycle();
      knapp.setText(autoSpeed.symbol());
      sfx.play('click');
    });
    const unsub = autoSpeed.subscribe(() => knapp.setText(autoSpeed.symbol()));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, unsub);
  }

  private muteSymbol(): string {
    return audioManager.erAv ? '🔇' : '🔊';
  }

  private buildStat(
    x: number,
    label: string,
    valueFn: (s: GameStateData) => string,
    capture: (t: Phaser.GameObjects.Text) => void,
  ): void {
    this.add
      .text(x, 14, label.toUpperCase(), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#8B5E3C',
        fontStyle: '600',
      })
      .setOrigin(0, 0);

    const value = this.add
      .text(x, 38, valueFn(gameState.snapshot), {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);

    capture(value);
  }

  private refresh(snap: GameStateData): void {
    this.kronerText?.setText(this.formatKroner(snap.kroner));
    this.dagText?.setText(String(snap.dag));
    this.sesongText?.setText(sesongLabel(snap.dag));
    this.tierChipText?.setText(tierChipLabel(snap));
  }

  private formatKroner(n: number): string {
    return `${n.toLocaleString('nb-NO')} kr`;
  }
}

function sesongLabel(dag: number): string {
  const s = sesongForDag(dag);
  return `${SESONG_NAVN[s]} · år ${aaret(dag)}`;
}

function tierChipLabel(snap: GameStateData): string {
  return `Tier ${snap.tier} · ${omdommeNivaa(snap.omdomme)}`;
}

export function omdommeNivaa(verdi: number): string {
  if (verdi >= 85) return 'Stjerne';
  if (verdi >= 60) return 'Anerkjent';
  if (verdi >= 30) return 'Etablert';
  return 'Ny';
}
